import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ElementRef,
  ViewChild,
  signal,
  effect,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { BehaviorSubject, debounceTime, distinctUntilChanged } from 'rxjs';
import { DropdownService } from 'src/utils/dropdown.service';

export interface MultiselectOption {
  value: string | number;
  label: string;
  active: boolean;
}

@Component({
  selector: 'multiselect',
  imports: [CommonModule, FormsModule, CheckboxComponent],
  templateUrl: './multiselect.component.html',
  standalone: true,
})
export class MultiselectComponent {
  @Input() options: MultiselectOption[] = [];
  @Input() icon: string = 'fi fi-rr-filter';
  @Input() placeholder: string = 'Seleccione las opciones deseadas';
  @Output() optionsSelectedChange = new EventEmitter<MultiselectOption[]>();
  @Output() optionSelectedChange = new EventEmitter<MultiselectOption>();
  @Output() searchChange = new EventEmitter<string>();

  private searchTermSubject = new BehaviorSubject<string>('');
  searchTerm$ = this.searchTermSubject.asObservable();
  isOpen = signal(false);
  dropdownPosition = 'left-0';
  dropdownStyles: { [key: string]: string } = {};
  dropdownId: string;

  @ViewChild('selectContainer', { static: false }) selectContainer!: ElementRef;

  constructor(private dropdownService: DropdownService) {
    this.dropdownId = `multiselect-${Math.random().toString(36).substr(2, 9)}`;

    effect(() => {
      const openDropdownId = this.dropdownService.getOpenDropdown()();
      
      if (openDropdownId === this.dropdownId) {
        this.isOpen.set(true);
        this.searchTerm = '';
        this.calculateDropdownPosition();
      } else {
        this.isOpen.set(false);
      }
    });

    this.searchTermSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((term) => this.searchChange.emit(term));
  }

  get searchTerm(): string {
    return this.searchTermSubject.value;
  }

  set searchTerm(value: string) {
    this.searchTermSubject.next(value);
  }

  get filteredOptions() {
    const searchValue = this.searchTermSubject.value;
    return this.options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }

  toggleDropdown() {
    this.dropdownService.toggleDropdown(this.dropdownId);
  }

  clearSearch() {
    this.searchTerm = '';
  }

  private calculateDropdownPosition() {
    if (this.selectContainer) {
      const rect = this.selectContainer.nativeElement.getBoundingClientRect();
      const screenWidth = window.innerWidth;
      const isMobile = screenWidth < 768;
      const dropdownWidth = isMobile ? 327 : 440;
      const margin = 16;
      this.dropdownStyles = {};
      
      if (isMobile) {
        if (rect.left > 200 && rect.right > 200) {
          this.dropdownPosition = 'left-[-280px]';
        } else {
          this.dropdownPosition = 'left-4 right-4';
        }
      } else {
        const spaceRight = screenWidth - rect.left;        
        if (spaceRight >= dropdownWidth + margin) {
          this.dropdownPosition = 'left-0';
          this.dropdownStyles = { width: `${dropdownWidth}px` };
        } else {
          this.dropdownPosition = 'right-0';
          this.dropdownStyles = { width: `${dropdownWidth}px` };
        }
      }
    }
  }

  changeActiveForOption(option: MultiselectOption) {
  option.active = !option.active;
  this.optionSelectedChange.emit(option);
  const selected = this.options.filter(o => o.active);
  this.optionsSelectedChange.emit(selected);
}

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.select-container')) {
      if (this.isOpen()) {
        this.optionsSelectedChange.emit(
          this.options.filter((option) => option.active)
        );
      }
      this.dropdownService.closeDropdown();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: Event) {
    if (this.isOpen()) {
      this.calculateDropdownPosition();
    }
  }
}